import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import useIsMobile from '../hooks/useIsMobile';
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Typography,
  Chip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tabs,
  Tab,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from '@mui/material';
// Removed MUI X date pickers due to version compatibility issues
import {
  Edit as EditIcon,
  Save as SaveIcon,
  Cancel as CancelIcon,
  Person as PersonIcon,
  Assignment as AssignmentIcon,
  Schedule as ScheduleIcon,
  Send as SendIcon,
  MenuBook as MenuBookIcon,
  PersonAdd as PersonAddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

interface Student {
  id: number;
  first_name: string;
  last_name: string;
  full_name: string;
  grade_level?: string;
  reading_level?: number;
  email?: string;
}

interface Assignment {
  id: number;
  document_id: number;
  student_ids: number[];
  assigned_by_user_id: number;
  assignment_date: string;
  due_date: string | null;
  assignment_type: 'submittable' | 'informational';
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Document {
  id: number;
  name: string;
  description?: string;
  original_filename: string;
}

interface AssignmentViewEditDialogProps {
  open: boolean;
  onClose: () => void;
  document: Document | null;
  onSave?: () => void;
  onOpenAssign?: () => void;
}

const AssignmentViewEditDialog: React.FC<AssignmentViewEditDialogProps> = ({
  open,
  onClose,
  document,
  onSave,
  onOpenAssign
}) => {
  const { token, isAuthenticated } = useAuth();
  const { t } = useTranslation();
  const { isMobile } = useIsMobile();

  // State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [tab, setTab] = useState(0);
  const [isEditing, setIsEditing] = useState(false);
  
  // Assignment data
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [currentAssignment, setCurrentAssignment] = useState<Assignment | null>(null);
  const [students, setStudents] = useState<Student[]>([]);
  
  // Edit form data
  const [editName, setEditName] = useState('');
  const [editDescription, setEditDescription] = useState('');
  const [editDueDate, setEditDueDate] = useState<string>('');
  const [editAssignmentType, setEditAssignmentType] = useState<'submittable' | 'informational'>('submittable');

  // Fetch assignments when dialog opens
  useEffect(() => {
    if (open && document && token && isAuthenticated) {
      fetchAssignments();
    }
  }, [open, document, token, isAuthenticated]);

  // Load document data when dialog opens or document changes
  useEffect(() => {
    if (document) {
      setEditName(document.name || '');
      setEditDescription(document.description || '');
      // Initialize due date from assignment if it exists, otherwise empty
      if (currentAssignment?.due_date) {
        setEditDueDate(new Date(currentAssignment.due_date).toISOString().slice(0, 16));
      } else {
        setEditDueDate('');
      }
      setEditAssignmentType(currentAssignment?.assignment_type || 'submittable');
    }
  }, [document, currentAssignment]);

  const fetchAssignments = async () => {
    if (!document || !token) return;
    
    try {
      setLoading(true);
      setError(null);
      
      const response = await fetch(`/api/documents/${document.id}/assignments`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });
      
      if (!response.ok) throw new Error('Failed to fetch assignments');
      
      const data = await response.json();
      if (data.success) {
        setAssignments(data.assignments);
        if (data.assignments.length > 0) {
          setCurrentAssignment(data.assignments[0]); // Show most recent assignment
          await fetchAssignedStudents(data.assignments[0].student_ids);
        }
      } else {
        throw new Error(data.error || 'Failed to fetch assignments');
      }
    } catch (error) {
      console.error('Error fetching assignments:', error);
      setError('Failed to load assignment details');
    } finally {
      setLoading(false);
    }
  };

  const fetchAssignedStudents = async (studentIds: number[]) => {
    if (!studentIds.length || !token) return;
    
    try {
      // Fetch student details - we'll need to implement this endpoint
      const response = await fetch('/api/students/by-ids', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ student_ids: studentIds }),
      });
      
      if (!response.ok) throw new Error('Failed to fetch students');
      
      const data = await response.json();
      if (data.success) {
        setStudents(data.students);
      }
    } catch (error) {
      console.error('Error fetching students:', error);
      // Don't set error for this, just log it
    }
  };

  const handleSaveEdit = async () => {
    if (!document || !token) return;
    
    try {
      setSaving(true);
      setError(null);
      
      // First, update the document details
      const documentUpdateData = {
        name: editName,
        description: editDescription
      };
      
      const documentResponse = await fetch(`/api/documents/${document.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(documentUpdateData),
      });
      
      if (!documentResponse.ok) throw new Error('Failed to update document');
      
      // If there's an assignment, update it
      if (currentAssignment) {
        const assignmentUpdateData: any = {
          assignment_type: editAssignmentType,
        };
        if (editDueDate) {
          assignmentUpdateData.due_date = new Date(editDueDate).toISOString();
        }
        
        const assignmentResponse = await fetch(`/api/documents/${document.id}/assignments/${currentAssignment.id}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(assignmentUpdateData),
        });
        
        if (!assignmentResponse.ok) throw new Error('Failed to update assignment due date');
      }
      
      setIsEditing(false);
      await fetchAssignments(); // Refresh data
      if (onSave) onSave();
      
    } catch (error) {
      console.error('Error updating document/assignment:', error);
      setError('Failed to update document details');
    } finally {
      setSaving(false);
    }
  };

  const handleClose = () => {
    setTab(0);
    setIsEditing(false);
    setCurrentAssignment(null);
    setAssignments([]);
    setStudents([]);
    setError(null);
    onClose();
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  if (!document) return null;

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      fullScreen={isMobile}
    >
      <DialogTitle>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <AssignmentIcon />
          Assignment Details: {document.name}
        </Box>
      </DialogTitle>
      
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography>Loading document details...</Typography>
          </Box>
        ) : (
          <>
            <Tabs value={tab} onChange={(_, newValue) => setTab(newValue)} sx={{ mb: 2 }}>
              <Tab label="Document Details" />
              {assignments.length > 0 && <Tab label="Assigned Students" />}
            </Tabs>

            {tab === 0 && (
              <Card>
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Typography variant="h6">Document Information</Typography>
                    {!isEditing ? (
                      <Button
                        startIcon={<EditIcon />}
                        onClick={() => setIsEditing(true)}
                        variant="outlined"
                        size="small"
                      >
                        Edit
                      </Button>
                    ) : (
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Button
                          startIcon={<SaveIcon />}
                          onClick={handleSaveEdit}
                          variant="contained"
                          size="small"
                          disabled={saving}
                        >
                          {saving ? 'Saving...' : 'Save'}
                        </Button>
                        <Button
                          startIcon={<CancelIcon />}
                          onClick={() => setIsEditing(false)}
                          variant="outlined"
                          size="small"
                          disabled={saving}
                        >
                          Cancel
                        </Button>
                      </Box>
                    )}
                  </Box>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Document Name</Typography>
                      {isEditing ? (
                        <TextField
                          value={editName}
                          onChange={(e) => setEditName(e.target.value)}
                          fullWidth
                          variant="outlined"
                          size="small"
                          required
                        />
                      ) : (
                        <Typography>{document.name}</Typography>
                      )}
                    </Box>

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                      {isEditing ? (
                        <TextField
                          value={editDescription}
                          onChange={(e) => setEditDescription(e.target.value)}
                          multiline
                          rows={3}
                          fullWidth
                          variant="outlined"
                          size="small"
                          placeholder="Add a description for this document..."
                        />
                      ) : (
                        <Typography>{document.description || 'No description provided'}</Typography>
                      )}
                    </Box>

                    {assignments.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">Assignment Due Date</Typography>
                        {isEditing ? (
                          <TextField
                            type="datetime-local"
                            value={editDueDate}
                            onChange={(e) => setEditDueDate(e.target.value)}
                            size="small"
                            fullWidth
                            helperText="Only applies to assigned students"
                            InputLabelProps={{
                              shrink: true,
                            }}
                          />
                        ) : (
                          <Typography>
                            {currentAssignment?.due_date ? (
                              <>
                                <ScheduleIcon sx={{ fontSize: 16, mr: 1, verticalAlign: 'middle' }} />
                                {formatDate(currentAssignment.due_date)}
                              </>
                            ) : (
                              'No due date set for assignment'
                            )}
                          </Typography>
                        )}
                      </Box>
                    )}

                    {assignments.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" color="text.secondary">
                          {t('assignmentDialog.assignmentType', 'Assignment Type')}
                        </Typography>
                        {isEditing ? (
                          <>
                            <ToggleButtonGroup
                              value={editAssignmentType}
                              exclusive
                              onChange={(_, value) => { if (value) setEditAssignmentType(value); }}
                              size="small"
                              fullWidth
                              aria-label={t('assignmentDialog.assignmentType', 'Assignment Type')}
                            >
                              <ToggleButton value="submittable" sx={{ textTransform: 'none', gap: 1 }}>
                                <SendIcon fontSize="small" />
                                {t('assignmentDialog.submittable', 'Submittable')}
                              </ToggleButton>
                              <ToggleButton value="informational" sx={{ textTransform: 'none', gap: 1 }}>
                                <MenuBookIcon fontSize="small" />
                                {t('assignmentDialog.informational', 'Informational')}
                              </ToggleButton>
                            </ToggleButtonGroup>
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                              {editAssignmentType === 'submittable'
                                ? t('assignmentDialog.submittableHint', 'Students will be able to submit work for this assignment.')
                                : t('assignmentDialog.informationalHint', 'Read-only material. Students can view the document and take quizzes, but won\'t see a Submit Work option.')}
                            </Typography>
                          </>
                        ) : (
                          <Chip
                            icon={currentAssignment?.assignment_type === 'informational' ? <MenuBookIcon /> : <SendIcon />}
                            label={currentAssignment?.assignment_type === 'informational'
                              ? t('assignmentDialog.informational', 'Informational')
                              : t('assignmentDialog.submittable', 'Submittable')}
                            size="small"
                            variant="outlined"
                            color={currentAssignment?.assignment_type === 'informational' ? 'info' : 'default'}
                          />
                        )}
                      </Box>
                    )}

                    <Divider />

                    <Box>
                      <Typography variant="subtitle2" color="text.secondary">Document Stats</Typography>
                      <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
                        {assignments.length > 0 ? (
                          <>
                            <Chip 
                              icon={<PersonIcon />} 
                              label={`${students.length} Students Assigned`} 
                              size="small" 
                            />
                            <Chip 
                              label={`Assigned ${formatDate(currentAssignment?.created_at || '')}`} 
                              size="small" 
                              variant="outlined"
                            />
                          </>
                        ) : (
                          <Chip 
                            label="Not yet assigned to students" 
                            size="small" 
                            variant="outlined"
                            color="default"
                          />
                        )}
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Card>
            )}

            {tab === 1 && assignments.length > 0 && (
              <Card>
                <CardContent>
                  <Typography variant="h6" sx={{ mb: 2 }}>
                    Assigned Students ({students.length})
                  </Typography>
                  {students.length === 0 ? (
                    <Typography color="text.secondary">Loading student information...</Typography>
                  ) : (
                    <List>
                      {students.map((student, index) => (
                        <React.Fragment key={student.id}>
                          <ListItem>
                            <ListItemText
                              primary={student.full_name}
                              secondary={
                                <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                                  {student.grade_level && (
                                    <Chip label={student.grade_level} size="small" />
                                  )}
                                  {student.reading_level && (
                                    <Chip label={`Level ${student.reading_level}`} size="small" variant="outlined" />
                                  )}
                                  {student.email && (
                                    <Typography variant="caption" color="text.secondary">
                                      {student.email}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < students.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ justifyContent: 'space-between' }}>
        {onOpenAssign && (
          <Button
            startIcon={<PersonAddIcon />}
            onClick={() => {
              handleClose();
              onOpenAssign();
            }}
            variant="outlined"
            aria-label={t('classWork.assignToStudents', 'Assign to Students')}
          >
            {t('classWork.assignToStudents', 'Assign to Students')}
          </Button>
        )}
        {!onOpenAssign && <Box />}
        <Button onClick={handleClose}>{t('common.close', 'Close')}</Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssignmentViewEditDialog;
